// Water areas (lakes, rivers, sea) around a place from OpenFreeMap's vector tiles: OpenStreetMap data in the
// OpenMapTiles schema, served from a CDN with no key. Only the "water" layer is read. No DOM, so tests can run it.
import type { LatLon, WaterArea } from "./shelter.ts";

export const WATER_ZOOM = 12; // ~4,7 km tiles in Finland; shapes accurate to a few metres, plenty for a 50 m grid

/* ---------- a minimal protobuf / Mapbox Vector Tile reader ---------- */
class Pbf {
  pos = 0;
  readonly buf: Uint8Array;
  end: number;
  constructor(buf: Uint8Array, end = buf.length) { this.buf = buf; this.end = end; }
  varint(): number {
    let v = 0, shift = 0, b: number;
    do { b = this.buf[this.pos++]; v += (b & 0x7f) * 2 ** shift; shift += 7; } while (b & 0x80);
    return v;
  }
  /** Skips a field of the given wire type. */
  skip(type: number): void {
    if (type === 0) this.varint();
    else if (type === 1) this.pos += 8;
    else if (type === 2) { const len = this.varint(); this.pos += len; } // (not `pos += varint()`: that adds to the old pos)
    else if (type === 5) this.pos += 4;
    else throw new Error("bad wire type " + type);
  }
  sub(): Pbf { const len = this.varint(), p = new Pbf(this.buf, this.pos + len); p.pos = this.pos; this.pos += len; return p; }
  string(): string { const len = this.varint(), s = new TextDecoder().decode(this.buf.subarray(this.pos, this.pos + len)); this.pos += len; return s; }
  packed(): number[] { const p = this.sub(), out: number[] = []; while (p.pos < p.end) out.push(p.varint()); return out; }
}
const zigzag = (n: number) => (n % 2 ? -(n + 1) / 2 : n / 2);

/** The polygons of a tile's "water" layer, each as rings in tile units (0..extent), with its class (lake, ocean, river...). */
function waterPolygons(buf: Uint8Array): { extent: number; polys: { rings: [number, number][][]; cls: string }[] } {
  const tile = new Pbf(buf);
  while (tile.pos < tile.end) {
    const tag = tile.varint();
    if (tag >> 3 !== 3 || (tag & 7) !== 2) { tile.skip(tag & 7); continue; }
    const layer = tile.sub();
    let name = "", extent = 4096;
    const features: Pbf[] = [], keys: string[] = [], values: string[] = [];
    while (layer.pos < layer.end) {
      const t = layer.varint(), f = t >> 3;
      if (f === 1) name = layer.string();
      else if (f === 2) features.push(layer.sub());
      else if (f === 3) keys.push(layer.string());
      else if (f === 4) { // a Value message: only its string form matters here
        const v = layer.sub();
        let s = "";
        while (v.pos < v.end) { const vt = v.varint(); if (vt >> 3 === 1 && (vt & 7) === 2) s = v.string(); else v.skip(vt & 7); }
        values.push(s);
      }
      else if (f === 5) extent = layer.varint();
      else layer.skip(t & 7);
    }
    if (name !== "water") continue;
    const classKey = keys.indexOf("class");
    const polys: { rings: [number, number][][]; cls: string }[] = [];
    for (const feat of features) {
      let type = 0, geom: number[] = [], cls = "";
      while (feat.pos < feat.end) {
        const t = feat.varint(), f = t >> 3;
        if (f === 2) { const tags = feat.packed(); for (let i = 0; i + 1 < tags.length; i += 2) if (tags[i] === classKey) cls = values[tags[i + 1]] ?? ""; }
        else if (f === 3) type = feat.varint();
        else if (f === 4) geom = feat.packed();
        else feat.skip(t & 7);
      }
      if (type !== 3) continue; // polygons only
      const rings: [number, number][][] = [];
      let x = 0, y = 0, ring: [number, number][] = [];
      for (let i = 0; i < geom.length;) {
        const cmd = geom[i] & 7, count = geom[i++] >> 3;
        if (cmd === 7) { if (ring.length) { ring.push(ring[0]); rings.push(ring); ring = []; } continue; }
        for (let k = 0; k < count; k++) {
          x += zigzag(geom[i++]); y += zigzag(geom[i++]);
          if (cmd === 1 && ring.length) { rings.push(ring); ring = []; }
          ring.push([x, y]);
        }
      }
      if (ring.length) rings.push(ring);
      polys.push({ rings, cls });
    }
    return { extent, polys };
  }
  return { extent: 4096, polys: [] };
}

/* ---------- tiles ↔ coordinates ---------- */
const tileX = (lon: number, z: number) => ((lon + 180) / 360) * 2 ** z;
const tileY = (lat: number, z: number) => ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) * 2 ** z;
const lonOf = (x: number, z: number) => (x / 2 ** z) * 360 - 180;
const latOf = (y: number, z: number) => (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / 2 ** z))) * 180) / Math.PI;

/** The tiles covering a square of `radiusM` around the centre. */
export function tilesAround(center: LatLon, radiusM: number, z = WATER_ZOOM): { x: number; y: number; z: number }[] {
  const dLat = radiusM / 110_540, dLon = radiusM / (111_320 * Math.cos((center.lat * Math.PI) / 180));
  const x0 = Math.floor(tileX(center.lon - dLon, z)), x1 = Math.floor(tileX(center.lon + dLon, z));
  const y0 = Math.floor(tileY(center.lat + dLat, z)), y1 = Math.floor(tileY(center.lat - dLat, z));
  const out: { x: number; y: number; z: number }[] = [];
  for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) out.push({ x, y, z });
  return out;
}

/** A tile's water areas as rings of coordinates (holes included as further rings). Rivers are marked flowing:
 *  nets go in lakes and the sea, so "Net for the night" leaves them out. */
export function waterAreas(buf: Uint8Array, t: { x: number; y: number; z: number }): WaterArea[] {
  const { extent, polys } = waterPolygons(buf);
  return polys.filter((p) => p.cls !== "swimming_pool").map((p) => ({ flowing: p.cls === "river",
    rings: p.rings.map((ring) => ring.map(([px, py]) => ({ lat: latOf(t.y + py / extent, t.z), lon: lonOf(t.x + px / extent, t.z) }))) }));
}

/** The tile address template, from OpenFreeMap's TileJSON (its path changes with each weekly data update). */
export async function tileTemplate(fetchFn: typeof fetch = fetch): Promise<string> {
  const tj = (await (await fetchFn("https://tiles.openfreemap.org/planet", { signal: AbortSignal.timeout(20000) })).json()) as { tiles?: string[] };
  if (!tj.tiles?.[0]) throw new Error("no tile url");
  return tj.tiles[0];
}
